import { Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useUser } from '../../auth/useUser';
import { useTheme } from '../../theme/ThemeContext';
import Dropdown, { DropdownItem } from '../ui/Dropdown';
import CreditWidget from '../ui/CreditWidget';
const logoDark = '/logo_darkmode.png';
const logoLight = '/logo_lightmode.png';

export default function Header() {
  const { loginWithRedirect, logout } = useAuth0();
  const { isAuthenticated, isGuest, user, isLoading } = useUser();
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  const ThemeToggle = ({ className = "" }) => (
    <button
      onClick={toggleTheme}
      className={`p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-all ${className}`}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {isDark ? (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
        </svg>
      ) : (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
        </svg>
      )}
    </button>
  );

  // Switch logo based on the current theme
  // const logoImg = theme === 'light' ? logoLight : logoDark;

  const handleSignup = () => {
    loginWithRedirect({
      authorizationParams: {
        screen_hint: 'signup',
      },
      appState: { returnTo: window.location.pathname, wasSignup: true },
    });
  };

  const handleLogin = () => {
    loginWithRedirect();
  };

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  return (
    <header className="relative z-50 flex-shrink-0 header-glass">
      <div className="container-page py-3">
        <div className="flex items-center justify-between">
          {/* Logo and branding */}
          <Link to="/" className="flex items-center hover:opacity-80 transition-opacity" aria-label="AuraIcon Home">
            <img
              src={isDark ? logoDark : logoLight}
              alt="AuraIcon"
              className="h-8 w-auto"
            />
          </Link>

          {/* Auth buttons */}
          <nav className="flex items-center gap-3">
            <CreditWidget />
            {isLoading ? (
              <div className="h-9 w-24 bg-slate-200 dark:bg-white/5 rounded-xl animate-pulse border border-slate-300 dark:border-white/10" />
            ) : isAuthenticated ? (
              <div className="flex items-center gap-3">
                <Dropdown
                  align="right"
                  trigger={
                    <button className="flex items-center gap-2 hover:bg-slate-100 dark:hover:bg-white/5 py-1.5 px-3 rounded-lg transition-colors group">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-violet-500/20 to-fuchsia-500/20 flex items-center justify-center text-violet-600 dark:text-violet-300 font-bold text-xs ring-2 ring-transparent group-hover:ring-violet-500/20 transition-all">
                        {user.name?.[0] || user.email?.[0] || 'U'}
                      </div>
                      <span className="hidden sm:inline text-sm font-medium text-slate-700 dark:text-slate-200 group-hover:text-slate-900 dark:group-hover:text-white">
                        {user.name || user.email || 'User'}
                      </span>
                      <svg
                        className="w-4 h-4 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300 transition-colors"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                      </svg>
                    </button>
                  }
                >
                  <div className="px-1 py-1">
                    <DropdownItem>
                      <Link to="/profile" className="flex items-center w-full">
                        <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                        </svg>
                        Profile
                      </Link>
                    </DropdownItem>
                    <DropdownItem onClick={toggleTheme}>
                      {theme === 'dark' ? (
                        <>
                          <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
                          </svg>
                          Light Mode
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
                          </svg>
                          Dark Mode
                        </>
                      )}
                    </DropdownItem>
                    <div className="h-px bg-slate-100 dark:bg-white/10 my-1" />
                    <DropdownItem onClick={handleLogout} danger>
                      <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
                      </svg>
                      Log out
                    </DropdownItem>
                  </div>
                </Dropdown>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <ThemeToggle className="mr-1" />
                <button
                  onClick={handleLogin}
                  className="btn-secondary px-3 py-1.5 text-sm"
                >
                  Log in
                </button>
                <button
                  onClick={handleSignup}
                  className="btn-primary px-4 py-2 text-sm"
                >
                  Sign up
                </button>
              </div>
            )}
          </nav>
        </div>
      </div>
    </header>
  );
}
